import { useT } from "@/i18n";
import { findSection } from "@/lib/dashboard-helpers";
import { resolveLocalized, type DisplayLabel } from "@pervasivemind/dashboard-render";
import { BilingualInput } from "@/pages/builder/components/BilingualInput";
import { Field } from "@/pages/builder/components/Field";
import { StructuralRow } from "@/pages/builder/inspector/StructuralRow";
import type { UseBuilderDraft } from "@/hooks/useBuilderDraft";

interface SectionInspectorProps {
  draft: UseBuilderDraft;
  sectionId: string;
}

/**
 * Read-only inspector for a selected section header.
 *
 * Editable: bilingual ``title`` + ``subtitle`` (admin curates the
 * visible labels). Read-only: section item list (with item kind +
 * label preview), section-scoped filter list, section ``kind``
 * (regular vs drill).
 *
 * Iter 21 — replaces ``SectionSettings``. Structural changes (add /
 * remove / reorder items, change filters) flow through Claude via
 * the report-builder skill.
 */
export function SectionInspector({
  draft,
  sectionId,
}: SectionInspectorProps): JSX.Element | null {
  const { t, locale } = useT();
  const section = findSection(draft.dashboard, sectionId);
  if (!section) return null;

  const itemLabel = (
    item: (typeof section.items)[number],
  ): string => {
    const props = (item.props ?? {}) as Record<string, unknown>;
    const display =
      (props.label as DisplayLabel | undefined) ??
      (props.title as DisplayLabel | undefined);
    if (display) {
      const resolved = resolveLocalized(display, locale);
      if (resolved) return resolved;
    }
    if (typeof props.content === "string" && props.content) {
      return props.content.slice(0, 32);
    }
    return `[${item.element}]`;
  };

  return (
    <div className="space-y-4">
      <SectionLabel text={t("inspector.section.editable")} />
      <Field label={t("editor.section.titleLabel")}>
        <BilingualInput
          value={section.title as DisplayLabel}
          onChange={(next) => draft.updateSection(sectionId, { title: next })}
        />
      </Field>
      <Field label={t("editor.section.subtitleLabel")}>
        <BilingualInput
          value={section.subtitle as DisplayLabel}
          onChange={(next) =>
            draft.updateSection(sectionId, { subtitle: next })
          }
        />
      </Field>
      <SectionLabel text={t("inspector.section.structural")} />
      <StructuralRow label={t("inspector.section.id")} value={section.id} mono />
      <StructuralRow
        label={t("inspector.section.kind")}
        value={section.kind ?? "section"}
      />
      {section.kind === "drill" && section.drill_id && (
        <StructuralRow
          label={t("inspector.section.drillId")}
          value={section.drill_id}
          mono
        />
      )}

      {section.items.length > 0 && (
        <StructuralRow
          label={t("inspector.section.items", { count: section.items.length })}
        >
          <ul className="space-y-1">
            {section.items.map((item) => (
              <li
                key={item.id}
                onClick={() => draft.selectItem(sectionId, item.id)}
                className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 hover:bg-neutral-100"
              >
                <span className="rounded bg-neutral-200 px-1.5 py-0.5 text-xs text-neutral-600">
                  {item.element}
                </span>
                <span className="flex-1 truncate text-xs text-neutral-700">
                  {itemLabel(item)}
                </span>
                <span className="font-mono text-xs text-neutral-500">
                  w:{item.width}
                </span>
              </li>
            ))}
          </ul>
        </StructuralRow>
      )}

      {section.filters && section.filters.length > 0 && (
        <StructuralRow
          label={t("inspector.section.filters", {
            count: section.filters.length,
          })}
        >
          <ul className="space-y-1">
            {section.filters.map((f) => (
              <li
                key={f.key}
                className="flex items-center gap-2 text-xs text-neutral-700"
              >
                <span className="font-mono text-neutral-600">{f.key}</span>
                <span className="rounded bg-neutral-100 px-1.5 py-0.5 text-xs text-neutral-500">
                  {f.type ?? "select"}
                </span>
              </li>
            ))}
          </ul>
        </StructuralRow>
      )}
    </div>
  );
}

function SectionLabel({ text }: { text: string }): JSX.Element {
  return (
    <div className="mb-1 border-b border-neutral-200 pb-1">
      <span className="text-xs font-semibold text-neutral-600">
        {text}
      </span>
    </div>
  );
}
